import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A group of mutually exclusive choices, each a bordered row with its label and a muted helper
 * beneath. A NATIVE radio underneath (role, arrow-key model and label association come free; no
 * new package), drawn with the brand's tokens, in a row at least 44 px tall for a thumb.
 */
export function RadioGroup({
  legend,
  children,
  className,
}: {
  readonly legend: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className="mb-1 text-sm font-semibold">{legend}</legend>
      {children}
    </fieldset>
  );
}

export function RadioOption({
  name,
  label,
  help,
  checked,
  onSelect,
}: {
  readonly name: string;
  readonly label: string;
  readonly help?: string;
  readonly checked: boolean;
  readonly onSelect: () => void;
}) {
  const helpId = useId();
  return (
    <label
      className={cn(
        'border-border bg-card hover:bg-muted flex min-h-11 cursor-pointer items-start gap-3 rounded-xl border p-3',
        'has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-2 has-[:checked]:border-primary',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        {...(help === undefined ? {} : { 'aria-describedby': helpId })}
        className={cn(
          'border-input bg-card mt-0.5 size-5 shrink-0 cursor-pointer appearance-none rounded-full border-2',
          'checked:border-primary outline-none checked:border-[6px]',
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold">{label}</span>
        {help === undefined ? null : (
          <span id={helpId} className="text-muted-foreground text-sm">
            {help}
          </span>
        )}
      </span>
    </label>
  );
}
